// components/contribute/GamificationBadge.js
import { useState, useEffect } from 'react'
import gamificationService from '../../services/gamificationService'
import { useAuth } from '../../contexts/AuthContext'
import './GamificationBadge.css'

// Badge-Definitionen
const BADGES = [
  {
    id: 'first_contribution',
    name: 'Erste Schritte',
    description: 'Ersten Beitrag eingereicht',
    icon: '🌟',
    requirement: 1,
    color: '#4caf50'
  },
  {
    id: 'helpful_tech',
    name: 'Hilfreicher Techniker',
    description: '5 Beiträge eingereicht',
    icon: '🔧',
    requirement: 5,
    color: '#2196f3'
  },
  {
    id: 'knowledge_expert',
    name: 'Wissensexperte',
    description: '10 Beiträge eingereicht',
    icon: '🏆',
    requirement: 10,
    color: '#ff9800'
  },
  {
    id: 'master_tech',
    name: 'Meister-Techniker',
    description: '25 Beiträge eingereicht',
    icon: '👑',
    requirement: 25,
    color: '#9c27b0'
  },
  {
    id: 'legend',
    name: 'Legende',
    description: '50 Beiträge eingereicht',
    icon: '💎',
    requirement: 50,
    color: '#f44336'
  }
]

function GamificationBadge ({ userEmail, onStatsLoad }) {
  const { user } = useAuth()
  const [stats, setStats] = useState({
    totalContributions: 0,
    approvedContributions: 0,
    pendingContributions: 0,
    rank: 'Neuling',
    level: 1,
    points: 0
  })
  const [unlockedBadges, setUnlockedBadges] = useState([])
  const [showNewBadge, setShowNewBadge] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (userEmail && user) {
      loadUserStats()
    }
  }, [userEmail, user])

  const loadUserStats = async () => {
    try {
      const userStats = await gamificationService.getUserStats(
        userEmail,
        user.uid
      )
      setStats(userStats)

      // Berechne freigeschaltete Badges
      const unlocked = BADGES.filter(
        badge => userStats.totalContributions >= badge.requirement
      )
      setUnlockedBadges(unlocked)

      if (onStatsLoad) {
        onStatsLoad(userStats)
      }

      setLoading(false)
    } catch (error) {
      console.error('Error loading stats:', error)
      setLoading(false)
    }
  }

  const calculateProgress = () => {
    const nextBadge = BADGES.find(
      badge => stats.totalContributions < badge.requirement
    )
    if (!nextBadge) return 100

    const progress = (stats.totalContributions / nextBadge.requirement) * 100
    return Math.min(progress, 100)
  }

  const getNextBadge = () => {
    return BADGES.find(badge => stats.totalContributions < badge.requirement)
  }

  const getRankColor = () => {
    if (stats.level >= 5) return '#f44336'
    if (stats.level >= 4) return '#9c27b0'
    if (stats.level >= 3) return '#ff9800'
    if (stats.level >= 2) return '#2196f3'
    return '#4caf50'
  }

  if (loading) {
    return (
      <div className='gamification-badge loading'>
        <div className='loading-spinner'></div>
      </div>
    )
  }

  const nextBadge = getNextBadge()
  const progress = calculateProgress()

  return (
    <div className='gamification-badge'>
      {/* Header mit Rank und Level */}
      <div className='gam-header'>
        <div className='rank-badge' style={{ borderColor: getRankColor() }}>
          <span className='rank-icon'>⭐</span>
          <div className='rank-info'>
            <div className='rank-title'>{stats.rank}</div>
            <div className='rank-level'>Level {stats.level}</div>
          </div>
        </div>

        <div className='stats-summary'>
          <div className='stat-item'>
            <span className='stat-number'>{stats.totalContributions}</span>
            <span className='stat-label'>Beiträge</span>
          </div>
          <div className='stat-item'>
            <span className='stat-number'>{stats.points}</span>
            <span className='stat-label'>Punkte</span>
          </div>
        </div>
      </div>

      {/* Progress zum nächsten Badge */}
      {nextBadge && (
        <div className='next-badge-progress'>
          <div className='progress-header'>
            <span className='progress-label'>
              Nächstes Abzeichen: {nextBadge.name}
            </span>
            <span className='progress-count'>
              {stats.totalContributions}/{nextBadge.requirement}
            </span>
          </div>
          <div className='progress-bar-wrapper'>
            <div
              className='progress-bar-inner'
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Badges Grid */}
      <div className='badges-section'>
        <h4>Errungenschaften</h4>
        <div className='badges-grid'>
          {BADGES.map(badge => {
            const isUnlocked = unlockedBadges.some(b => b.id === badge.id)
            return (
              <div
                key={badge.id}
                className={`badge-item ${isUnlocked ? 'unlocked' : 'locked'}`}
                title={badge.description}
              >
                <div
                  className='badge-icon'
                  style={{
                    backgroundColor: isUnlocked ? badge.color : '#e0e0e0'
                  }}
                >
                  {isUnlocked ? badge.icon : '🔒'}
                </div>
                <div className='badge-name'>{badge.name}</div>
                {!isUnlocked && (
                  <div className='badge-requirement'>
                    {badge.requirement} Beiträge
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Motivations-Nachricht */}
      <div className='motivation-box'>
        {stats.totalContributions === 0 && (
          <p>
            🚀 <strong>Legen Sie los!</strong> Ihr erster Beitrag wartet auf
            Sie.
          </p>
        )}
        {stats.totalContributions > 0 && stats.totalContributions < 5 && (
          <p>
            💪 <strong>Großartig!</strong> Sie sind auf dem besten Weg zum
            "Hilfreicher Techniker" Badge!
          </p>
        )}
        {stats.totalContributions >= 5 && stats.totalContributions < 10 && (
          <p>
            🎯 <strong>Beeindruckend!</strong> Nur noch{' '}
            {10 - stats.totalContributions} Beiträge bis zum Wissensexperten!
          </p>
        )}
        {stats.totalContributions >= 10 && (
          <p>
            🌟 <strong>Sie sind fantastisch!</strong> Die Community profitiert
            enorm von Ihrem Wissen!
          </p>
        )}
      </div>

      {/* Neue Badge Animation (wenn freigeschaltet) */}
      {showNewBadge && (
        <div
          className='new-badge-overlay'
          onClick={() => setShowNewBadge(null)}
        >
          <div className='new-badge-popup'>
            <div className='confetti'>🎉</div>
            <div
              className='new-badge-icon'
              style={{ backgroundColor: showNewBadge.color }}
            >
              {showNewBadge.icon}
            </div>
            <h3>Neues Abzeichen freigeschaltet!</h3>
            <p className='badge-title'>{showNewBadge.name}</p>
            <p className='badge-desc'>{showNewBadge.description}</p>
            <button onClick={() => setShowNewBadge(null)}>Schließen</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default GamificationBadge
